import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PlayCircle, ArrowLeft, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { visitSession } from "@/lib/abandon";
import { useClientAuth } from "@/context/ClientAuthContext";

export default function LifeInMexico() {
  const { slug } = useParams();
  return slug ? <VideoDetail slug={slug} /> : <VideoGallery />;
}

function VideoGallery() {
  const [page, setPage] = useState(null);
  const [videos, setVideos] = useState([]);
  const [tag, setTag] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/content/page/life-in-mexico").then((r) => setPage(r.data)).catch(() => setPage({}));
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);
  const tags = [...new Set(videos.map((v) => v.tag).filter(Boolean))];
  const shown = tag ? videos.filter((v) => v.tag === tag) : videos;
  return (
    <div data-testid="page-life-in-mexico">
      <Seo title="Life in Mexico — Videos | Hola Movers" description="Short videos of neighborhoods, markets, everyday life and moving-day stories in Mérida, Mexico." path="/life-in-mexico" />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {page?.hero_image && <img src={page.hero_image} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-sand-100 via-sand-100/90 to-sand-100/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <h1 className="max-w-2xl text-4xl sm:text-5xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
            {page?.hero_title || "See life in Mexico before you move"}
          </h1>
          <p className="mt-5 max-w-xl text-lg text-charcoal-600">{page?.hero_subtitle || ""}</p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-12">
        {tags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="video-tag-filters">
            <button data-testid="video-tag-all" onClick={() => setTag("")}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${!tag ? "border-terracotta bg-terracotta text-white" : "border-sand-300 bg-sand-100 text-charcoal-700 hover:border-terracotta"}`}>All</button>
            {tags.map((t) => (
              <button key={t} data-testid={`video-tag-${t.replace(/\s+/g, "-")}`} onClick={() => setTag(t)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium capitalize transition-colors ${tag === t ? "border-terracotta bg-terracotta text-white" : "border-sand-300 bg-sand-100 text-charcoal-700 hover:border-terracotta"}`}>{t}</button>
            ))}
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="video-grid">
          {shown.map((v) => (
            <Link key={v.id} to={`/life-in-mexico/${v.slug}`} data-testid={`video-item-${v.slug}`}
              className="group overflow-hidden rounded-2xl border border-sand-200 bg-white hover:border-terracotta transition-colors">
              <div className="relative aspect-video overflow-hidden bg-sand-200">
                {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />}
                <span className="absolute inset-0 flex items-center justify-center">
                  <PlayCircle size={48} className="text-white drop-shadow-lg opacity-90" strokeWidth={1.2} />
                </span>
                {v.tag && <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-medium capitalize text-charcoal-700">{v.tag}</span>}
              </div>
              <div className="p-5">
                <h3 className="font-medium text-charcoal-800 group-hover:text-terracotta transition-colors">{v.title}</h3>
                {v.description && <p className="mt-1.5 text-sm text-charcoal-500 line-clamp-2">{v.description}</p>}
              </div>
            </Link>
          ))}
          {shown.length === 0 && <p className="text-charcoal-500">No videos yet — check back soon.</p>}
        </div>
      </section>
    </div>
  );
}

function VideoDetail({ slug }) {
  const [v, setV] = useState(undefined);
  const { client } = useClientAuth();
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/videos/${slug}`).then((r) => setV(r.data)).catch(() => setV(null));
  }, [slug]);
  useEffect(() => {
    if (client === undefined) return; // wait for auth resolution so views link to the client
    api.post(`/videos/${slug}/view`, { session_id: visitSession(), email: client?.email || "" }).catch(() => {});
  }, [slug, client]);
  if (v === undefined) return <div className="py-32 text-center text-charcoal-500">Loading…</div>;
  if (v === null) return (
    <div className="py-32 text-center">
      <p className="text-charcoal-600">That video isn't available.</p>
      <Link to="/life-in-mexico" className="mt-4 inline-block text-terracotta hover:underline">Browse all videos</Link>
    </div>
  );
  const shareUrl = window.location.href;
  const copy = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); };
  const directSrc = v.embed_url.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${v.embed_url}`
    : (/\.(mp4|mov|webm|m4v)(\?|$)/i.test(v.embed_url) ? v.embed_url : null);
  return (
    <div data-testid="page-video-detail" className="mx-auto max-w-4xl px-6 py-12 lg:py-16">
      <Seo title={`${v.title} | Life in Mexico — Hola Movers`} description={v.description || v.title} path={`/life-in-mexico/${v.slug}`} />
      <Link to="/life-in-mexico" data-testid="video-back" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-terracotta"><ArrowLeft size={15} /> All videos</Link>
      <h1 className="mt-4 text-3xl sm:text-4xl font-light text-charcoal-900">{v.title}</h1>
      {v.tag && <span className="mt-2 inline-block rounded-full bg-sand-200 px-3 py-1 text-xs font-medium capitalize text-charcoal-700">{v.tag}</span>}
      <div className="mt-6 overflow-hidden rounded-2xl border border-sand-200 bg-black aspect-video">
        {directSrc ? (
          <video data-testid="video-file-player" src={directSrc} controls className="h-full w-full" />
        ) : (
          <iframe data-testid="video-embed" src={v.embed_url} title={v.title} className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        )}
      </div>
      {v.description && <p className="mt-5 text-charcoal-600 leading-relaxed">{v.description}</p>}
      <div className="mt-6 flex flex-wrap gap-2">
        <button data-testid="video-share-copy" onClick={copy}
          className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-100 px-4 py-2 text-sm font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta transition-colors">
          <Share2 size={14} /> Copy link
        </button>
        <a data-testid="video-share-whatsapp" target="_blank" rel="noreferrer"
          href={`https://wa.me/?text=${encodeURIComponent(`${v.title} — ${shareUrl}`)}`}
          className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-100 px-4 py-2 text-sm font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta transition-colors">
          <MessageCircle size={14} /> Share on WhatsApp
        </a>
      </div>
      <div className="mt-10 rounded-2xl bg-sage-light p-6">
        <p className="font-medium text-charcoal-800">Picturing yourself here?</p>
        <p className="mt-1 text-sm text-charcoal-600">We help US families (and their pets) make this move calmly, step by step.</p>
        <Link to="/book" data-testid="video-book-cta" className="mt-4 inline-block rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover">Book an intro call</Link>
      </div>
    </div>
  );
}
